import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { TextField1 } from "app/assets";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Breadcrumb, SimpleCard } from "app/components";

import { PATH, LIVE_URL } from "../../../config";
import Loading from "app/components/MatxLoading";
import { GETUSER, GETALLUSERS, UPDATEUSER } from "app/redux/actions";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

const EditEmployee = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loggedInUser = useSelector((state) => state.user.user);

  const user = useSelector((state) => state.user);
  const [teamLeadList, setTeamLeadList] = useState([]);

  useEffect(() => {
    if (loggedInUser?.token) {
      if (id) {
        dispatch(GETUSER(id, loggedInUser));
      }
      dispatch(
        GETALLUSERS(
          { page: 1, limit: 1000, userType: "admin,teamlead" },
          loggedInUser
        )
      );
    }
  }, [dispatch, id, loggedInUser]);
  useEffect(() => {
    if (user?.allUsers?.users) {
      const filteredLeads = user.allUsers.users.filter(
        (lead) => lead.id !== id
      );
      setTeamLeadList(filteredLeads);
    }
  }, [user?.allUsers?.users, id]);
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    cnic: Yup.string()
      .matches(
        /(^\d{13}$)|(^\d{5}-\d{7}-\d{1}$)/,
        "Invalid CNIC. Use: 0000000000000 or 00000-0000000-0"
      )
      .required("CNIC is required"),
    phone: Yup.string()
      .matches(
        /(^0\d{10}$)|(^\d{4}-\d{7}$)|(^\+92\d{10}$)|(^\+92 \d{10}$)/,
        "Invalid phone. Use: 03xxxxxxxxx, 03xx-xxxxxxx, +923xxxxxxxxx, or +92 3xxxxxxxxx"
      )
      .required("Mobile is required"),
    account_no: Yup.string().required("Account Number is required"),
    joiningDate: Yup.date().required("Joining date is required").nullable(),
    employmentStatus: Yup.string().required("Employment status is required"),
    status: Yup.string().required("Status is required"),
    isTeamLead: Yup.boolean(),
    deviceId: Yup.string().required("Device ID is required"),
  });

  const onfailure = (message) => {
    toast.error(message);
  };
  const handleSubmit = (values) => {
    const formData = new FormData();
    for (const key in values) {
      if (values[key] instanceof File) {
        formData.append(key, values[key]);
      } else {
        formData.append(key, values[key] ?? "");
      }
    }
    formData.append("id", id);
    formData.append("userType", values.isTeamLead ? "teamlead" : "employee");

    const onSuccess = () => {
      toast.success("Employee updated successfully!");
      navigate(PATH.ALLEMPLOYEES);
    };
    const onFailure = (message) => {
      toast.error(message || "Error occurred while updating!");
    };

    dispatch(UPDATEUSER(formData, loggedInUser?.token, onSuccess, onFailure));
  };

  const handleFileUpload = (event, fieldName, setFieldValue) => {
    const file = event.target.files[0];
    if (!file) return;
    setFieldValue(fieldName, file);
    toast.success(`${fieldName.replace("_", " ")} selected successfully!`);
  };

  const handleRemoveFile = (fieldName, setFieldValue) => {
    setFieldValue(fieldName, null);
  };

  if (user.userLoading || !user?.employeeToEdit) {
    return <Loading />;
  }

  if (user.userError) {
    return <p>Failed To Get Employee Try Again</p>;
  }

  return (
    <SimpleCard>
      <Box>
        <Breadcrumb routeSegments={[{ name: "Edit Employee" }]} />
      </Box>
      <Formik
        initialValues={{
          name: user?.employeeToEdit?.name || "",
          email: user?.employeeToEdit?.email || "",
          deviceId: user?.employeeToEdit?.deviceId || "",
          cnic: user?.employeeToEdit?.cnic || "",
          phone: user?.employeeToEdit?.phone || "",
          account_no: user?.employeeToEdit?.account_no || "",
          joiningDate: user?.employeeToEdit?.joiningDate
            ? new Date(user?.employeeToEdit?.joiningDate)
              .toISOString()
              .split("T")[0]
            : "",
          status: user?.employeeToEdit?.status || "active",
          isTeamLead: user?.employeeToEdit?.isTeamLead || false,
          employmentStatus:
            user?.employeeToEdit?.employmentStatus || "Probation",
          teamLeadId: user?.employeeToEdit?.teamLeadId || "",
          reportTo: user?.employeeToEdit?.reportTo || "",
          cnic_front: user?.employeeToEdit?.cnic_front || null,
          cnic_back: user?.employeeToEdit?.cnic_back || null,
          isTerminated: user?.employeeToEdit?.isTerminated || false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          handleChange,
          handleSubmit,
          setFieldValue,
          errors,
          touched,
        }) => (
          <Box component="form" onSubmit={handleSubmit} sx={{ padding: 2 }}>
            <Grid container spacing={3}>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField1
                  fullWidth
                  name="name"
                  label="Name *"
                  value={values.name}
                  onChange={handleChange}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField1
                  fullWidth
                  name="email"
                  label="Email *"
                  value={values.email}
                  // disabled
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField1
                  fullWidth
                  name="cnic"
                  label="CNIC Number *"
                  value={values.cnic}
                  onChange={handleChange}
                  error={touched.cnic && Boolean(errors.cnic)}
                  helperText={touched.cnic && errors.cnic}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField1
                  fullWidth
                  name="phone"
                  label="Mobile Number *"
                  value={values.phone}
                  onChange={handleChange}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <label style={{ marginBottom: "5px", display: "block", color: "#666" }}>
                  Account Number *
                </label>
                <textarea
                  name="account_no"
                  rows="4"
                  value={values.account_no}
                  onChange={handleChange}
                  placeholder="Enter Account Number details..."
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "4px",
                    border: "1px solid #c4c4c4",
                    fontFamily: "inherit",
                    fontSize: "16px",
                    resize: "vertical"
                  }}
                />
                {touched.account_no && errors.account_no && (
                  <div style={{ color: "#d32f2f", fontSize: "0.75rem", marginLeft: "14px", marginTop: "3px" }}>
                    {errors.account_no}
                  </div>
                )}
              </Grid>
              {/* ADD THIS NEW GRID ITEM FOR DEVICE ID */}
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField1
                  fullWidth
                  name="deviceId"
                  label="Device ID *"
                  value={values.deviceId}
                  onChange={handleChange}
                  error={touched.deviceId && Boolean(errors.deviceId)}
                  helperText={touched.deviceId && errors.deviceId}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField1
                  fullWidth
                  name="joiningDate"
                  label="Joining Date *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.joiningDate}
                  onChange={handleChange}
                  error={touched.joiningDate && Boolean(errors.joiningDate)}
                  helperText={touched.joiningDate && errors.joiningDate}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField
                  select
                  fullWidth
                  name="employmentStatus"
                  label="Employment Status *"
                  value={values.employmentStatus}
                  onChange={handleChange}
                >
                  <MenuItem value="Probation">Probation</MenuItem>
                  <MenuItem value="Contractual">Contractual</MenuItem>
                  <MenuItem value="Internship">Internship</MenuItem>
                </TextField>
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField1
                  fullWidth
                  name="status"
                  label="Status"
                  select
                  SelectProps={{ native: true }}
                  value={values.status}
                  onChange={handleChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </TextField1>
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField
                  select
                  fullWidth
                  name="reportTo"
                  label="Assign Report To"
                  value={values.reportTo}
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(teamLeadList || []).map((lead) => (
                    <MenuItem key={lead.id} value={lead.id}>
                      {lead.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isTeamLead"
                      checked={values.isTeamLead}
                      onChange={(e) => {
                        handleChange(e);
                        if (e.target.checked) {
                          setFieldValue("teamLeadId", "");
                        }
                      }}
                    />
                  }
                  label="Is Team Lead"
                />
              </Grid>
              {!values.isTeamLead ? (
                <>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <TextField
                      select
                      fullWidth
                      name="teamLeadId"
                      label="Assign Team Lead (Optional)"
                      value={values.teamLeadId}
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>

                      {(teamLeadList || [])
                        .filter((user) => user.userType !== "admin")
                        .map((lead) => (
                          <MenuItem key={lead.id} value={lead.id}>
                            {lead.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isTerminated"
                          checked={values.isTerminated}
                          onChange={handleChange}
                        />
                      }
                      label="Terminate"
                    />
                  </Grid>
                </>
              ) : (
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isTerminated"
                        checked={values.isTerminated}
                        onChange={handleChange}
                      />
                    }
                    label="Terminate"
                  />
                </Grid>
              )}

              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFileIcon />}
                >
                  Upload CNIC Front
                  <input
                    type="file"
                    hidden
                    onChange={(event) =>
                      handleFileUpload(event, "cnic_front", setFieldValue)
                    }
                  />
                </Button>

                {/* Show selected file with remove */}
                {values.cnic_front && (
                  <Box mt={1} display="flex" alignItems="center" gap={2}>
                    {values.cnic_front instanceof File ? (
                      <img
                        src={URL.createObjectURL(values.cnic_front)}
                        alt="CNIC Front"
                        style={{ width: 100, height: "auto", borderRadius: 4 }}
                      />
                    ) : (
                      <img
                        src={`${LIVE_URL}public${values.cnic_front}`}
                        alt="CNIC Front"
                        style={{ width: 100, height: "auto", borderRadius: 4 }}
                      />
                    )}
                    <Button
                      size="small"
                      color="error"
                      onClick={() =>
                        handleRemoveFile("cnic_front", setFieldValue)
                      }
                    >
                      Remove
                    </Button>
                  </Box>
                )}
              </Grid>

              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFileIcon />}
                >
                  Upload CNIC Back
                  <input
                    type="file"
                    hidden
                    onChange={(event) =>
                      handleFileUpload(event, "cnic_back", setFieldValue)
                    }
                  />
                </Button>

                {/* Show selected file with remove */}
                {values.cnic_back && (
                  <Box mt={1} display="flex" alignItems="center" gap={2}>
                    {values.cnic_back instanceof File ? (
                      <img
                        src={URL.createObjectURL(values.cnic_back)}
                        alt="CNIC Back"
                        style={{ width: 100, height: "auto", borderRadius: 4 }}
                      />
                    ) : (
                      <img
                        src={`${LIVE_URL}public${values.cnic_back}`}
                        alt="CNIC Back"
                        style={{ width: 100, height: "auto", borderRadius: 4 }}
                      />
                    )}
                    <Button
                      size="small"
                      color="error"
                      onClick={() =>
                        handleRemoveFile("cnic_back", setFieldValue)
                      }
                    >
                      Remove
                    </Button>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                {user.updateLoading === true ? (
                  <Loading />
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={user.updateLoading}
                  >
                    {user.updateLoading ? "Updating..." : "Update Employee"}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </Formik>
    </SimpleCard>
  );
};

export default EditEmployee;
